import api from './api';

// Team Invitation API service
export const teamInvitationApi = {
  /**
   * Send team invitation to a user
   * @param {string} teamId - Team ID
   * @param {Object} invitationData - Invitation data
   * @param {string} invitationData.email - User email
   * @param {string} invitationData.roleKey - Role to assign
   * @param {string} invitationData.roleName - Role display name
   * @param {string} invitationData.message - Optional invitation message
   * @param {string} invitationData.clubId - Club ID for new user flow
   * @param {string} invitationData.programId - Program ID for new user flow
   * @returns {Promise} API response
   */
  sendInvitation: async (teamId, invitationData) => {
    try {
      const response = await api.post(`/api/teams/${teamId}/invite`, invitationData);
      return response;
        } catch (error) {
          // Re-throw with enhanced error information
          const enhancedError = new Error(error.message || 'Failed to send invitation');
          enhancedError.status = error.status;
          enhancedError.response = error.payload || error.response;
          enhancedError.data = error.payload || error.data;
          throw enhancedError;
        }
  },

  /**
   * Get pending invitations for a team
   * @param {string} teamId - Team ID
   * @returns {Promise} API response with pending invitations
   */
  getPendingInvitations: (teamId) => {
    return api.get(`/api/teams/${teamId}/invitations`);
  },

  /**
   * Get user's pending team invitations
   * @returns {Promise} API response with user's invitations
   */
  getUserInvitations: () => {
    return api.get('/api/teams/invitations/user');
  },

  /**
   * Respond to team invitation (accept/decline)
   * @param {string} invitationId - Invitation ID
   * @param {string} response - 'accepted' or 'declined'
   * @param {string} message - Optional response message
   * @returns {Promise} API response
   */
  respondToInvitation: (invitationId, response, message = '') => {
    return api.put(`/api/teams/invitations/${invitationId}/respond`, {
      response,
      message
    });
  },

  /**
   * Cancel team invitation
   * @param {string} invitationId - Invitation ID
   * @returns {Promise} API response
   */
  cancelInvitation: (invitationId) => {
    return api.del(`/api/teams/invitations/${invitationId}`);
  },

  /**
   * Search users by email for team invitation
   * @param {string} email - Email to search
   * @param {string} clubId - Club ID for context
   * @param {string} programId - Program ID for context
   * @returns {Promise} API response with user search results
   */
  searchUsersByEmail: async (email, clubId, programId) => {
    try {
      // Try the dedicated user search endpoint first
      const params = new URLSearchParams({
        email,
        clubId: clubId || '',
        programId: programId || ''
      });
      return await api.get(`/api/users/search/email?${params}`);
        } catch (error) {
          // Fallback: Try to search users through a different endpoint
          try {
            const response = await api.get(`/api/users?email=${encodeURIComponent(email)}`);
            
            if (response.success && response.users && response.users.length > 0) {
              const user = response.users[0];
              return {
                success: true,
                userType: USER_TYPE.EXISTING_USER,
                user: {
                  _id: user._id,
                  name: user.name || user.displayName || 'Unknown User',
                  email: user.email,
                  clubMembership: user.clubMembership || null,
                  programMembership: user.programMembership || null
                }
              };
            } else {
              return {
                success: true,
                userType: USER_TYPE.NEW_USER,
                user: null,
                message: 'User not found in system. They will need to join the club and program first.'
              };
            }
          } catch (fallbackError) {
            // Final fallback: Assume new user
            return {
              success: true,
              userType: USER_TYPE.NEW_USER,
              user: null,
              message: 'Unable to search users. Assuming new user - they will need to join the club and program first.'
            };
          }
        }
  },

  /**
   * Get available roles for team invitation
   * @param {string} teamId - Team ID
   * @returns {Promise} API response with available roles
   */
  getAvailableRoles: (teamId) => {
    return api.get(`/api/teams/${teamId}/roles`);
  }
};

// Team invitation constants
export const INVITATION_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled'
};

export const INVITATION_RESPONSE = {
  ACCEPT: 'accepted',
  DECLINE: 'declined'
};

export const USER_TYPE = {
  EXISTING_USER: 'existing_user',
  NEW_USER: 'new_user',
  CLUB_MEMBER: 'club_member',
  PROGRAM_MEMBER: 'program_member'
};

export default teamInvitationApi;
